import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { api, ApiError } from "@/lib/api";
import { useDataStamp } from "@/hooks/useDataStamp";
import type { FilterState } from "@/types/filters";
import type { SchedulingRow } from "@/types/scheduling";
import { EMPTY_RANGE, getPriorYearRange } from "@/lib/periods";

interface UseSchedulingDataResult {
  rows: SchedulingRow[];
  loading: boolean;
  /**
   * True during ANY request, including a background refetch that still has data
   * to show. loading covers only the first one. Compare mode needs the
   * distinction: with loading alone it can diff a settled result against one that
   * is mid-refresh and report a difference that is really a timing gap.
   */
  fetching: boolean;
  error: ApiError | Error | null;
}

export function useSchedulingData(
  filters: FilterState,
  /**
   * False on the rollup path, where nothing reads these rows. This is the switch
   * the whole rollup effort exists for: every other change made the raw rows
   * unnecessary, and this is what stops fetching them. At YTD they are ~268 MB of
   * JSON and ~1.3 s of parsing in every browser.
   */
  enabled = true,
  /**
   * The launched-before cohort, or null for every site. Applied HERE rather than
   * in the hooks downstream because this is the one place raw rows enter the app:
   * filtering once at the source means the metrics, the trends, the drill table,
   * the client-side thresholds and the NVA benchmark all honour the cutoff
   * without any of them knowing it exists.
   *
   * The rollup path needs no equivalent -- fetchRollupPayload cuts the same
   * cohort in SQL, so ineligible rows never arrive.
   */
  eligibleSites: ReadonlySet<string> | null = null
): UseSchedulingDataResult {
  const { customStart, customEnd, period } = filters;
  // Every tab shares one period now, so the fetched window is exactly the
  // selected one — it no longer has to be widened to cover a second period
  // state for Revenue and Cancellation.
  const pyRange =
    getPriorYearRange({ startDate: customStart, endDate: customEnd }, period) ?? EMPTY_RANGE;

  // Rides along in the key AND the URL so a rebuild defeats both TanStack's
  // staleTime and the response's max-age=300. Server-issued, so it works for every
  // open browser rather than only the one that pressed Refresh -- see
  // src/hooks/useDataStamp.ts.
  const { stamp: dataVersion, ready } = useDataStamp();

  const { data, isPending, isFetching, error } = useQuery({
    // Two conditions, and both are required. `enabled` is the caller's: false on
    // the rollup path, where nothing reads these rows. `ready` waits for the data
    // stamp, without which this fetches everything once under a placeholder and
    // again under the real value.
    //
    // They arrived from two branches that each added an `enabled` key here, and
    // git merged both -- legal JavaScript in which the last one silently wins, so
    // the stamp wait was being dropped and the double fetch was back. tsc caught
    // it; nothing else would have.
    enabled: enabled && ready,
    queryKey: ["scheduling", customStart, customEnd, dataVersion],
    queryFn: () => {
      const params = new URLSearchParams({
        start: customStart,
        end: customEnd,
        pyStart: pyRange.startDate,
        pyEnd: pyRange.endDate,
        v: dataVersion,
      });
      return api<SchedulingRow[]>(`/api/scheduling?${params}`);
    },
  });

  // Memoized on the fetched array, not recomputed per render: this walks ~137k
  // rows, and every hook downstream keys its own memo off the identity of what
  // comes back.
  const rows = useMemo(() => {
    if (!data) return [];
    if (!eligibleSites) return data;
    return data.filter((r) => r.location_id !== null && eligibleSites.has(r.location_id));
  }, [data, eligibleSites]);

  return {
    rows,
    // isPending stays true forever for a disabled query, which would leave the
    // dashboard on its loading screen with no request in flight. Disabled means
    // "nothing to wait for", not "still waiting".
    loading: enabled && isPending,
    fetching: enabled && isFetching,
    error: (error as ApiError | Error | null) ?? null,
  };
}
